import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { bankAccountApi, type BankAccountData } from '../../services/bankAccountApi';

function formatIban(iban: string): string {
  if (!iban) return '';
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

export default function BankAccountsList() {
  const [accounts, setAccounts] = useState<BankAccountData[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await bankAccountApi.getAll();
      setAccounts(data);
    } catch (error) {
      toast.error('Banka hesapları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleToggleActive = async (account: BankAccountData) => {
    try {
      await bankAccountApi.update(account.id!, { isActive: !account.isActive });
      toast.success(account.isActive ? 'Pasif yapıldı' : 'Aktif yapıldı');
      fetchAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Durum güncellenemedi');
    }
  };

  const handleDelete = async (id: string, bankName: string) => {
    if (!window.confirm(`${bankName} hesabını silmek istediğinize emin misiniz?`)) return;
    try {
      await bankAccountApi.remove(id);
      toast.success('Banka hesabı silindi');
      fetchAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Banka hesabı silinemedi');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Banka Hesapları</h1>
          <p className="text-gray-500 mt-1">Havale / EFT ile ödeme için banka hesaplarını yönetin</p>
        </div>
        <Link
          to="/admin/bank-accounts/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          + Yeni Hesap Ekle
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : accounts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">Henüz banka hesabı tanımlanmamış</p>
          <Link to="/admin/bank-accounts/new" className="text-blue-600 hover:underline">
            İlk banka hesabınızı ekleyin →
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Banka</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Hesap Sahibi</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">IBAN</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Sıra</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Durum</th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-600">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium">{account.bankName}</div>
                    {account.branch && <div className="text-xs text-gray-500">{account.branch}</div>}
                  </td>
                  <td className="px-6 py-4 text-sm">{account.accountHolder}</td>
                  <td className="px-6 py-4 font-mono text-sm">{formatIban(account.iban)}</td>
                  <td className="px-6 py-4 text-sm">{account.order}</td>
                  <td className="px-6 py-4">
                    {account.isActive ? (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        Aktif
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                        Pasif
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleActive(account)}
                        className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                      >
                        {account.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                      </button>
                      <Link
                        to={`/admin/bank-accounts/${account.id}/edit`}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Düzenle
                      </Link>
                      <button
                        onClick={() => handleDelete(account.id!, account.bankName)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
